import { useState } from "react";
import { useQuery } from "react-query";
import {
  MdOutlineInsights,
  MdOutlineInfo,
  MdTrendingUp,
  MdTrendingDown,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { CRM } from "../config/apiConfig";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
  SkeletonRows,
} from "../components/crm/Primitives";
import {
  SkeletonKpiStrip,
  SkeletonChart,
  SkeletonPanel,
} from "../components/crm/Skeletons";
import FunnelShape from "../components/crm/FunnelShape";
import {
  CanaisChart,
  CoortesChart,
  OnboardingChart,
} from "../components/crm/FunnelCharts";
import {
  formatMoney,
  formatNumber,
  formatPercent,
  toneOf,
} from "../components/crm/crmHelpers";

/*
  Funil de aquisição e conversão.

  A versão anterior era uma pilha de seis tabelas sem um único gráfico — e
  comparar "40% contra 85,7% contra 100%" lendo linhas é exatamente o trabalho
  que uma barra faz sozinha. Agora cada comparação virou desenho, e o número
  exato ficou atrás de "ver números" em cada bloco.

  A ordem é a da pergunta que se faz: **onde o funil vaza** (o funil em si,
  com a maior perda destacada), **de onde vem quem converte** (canais),
  **estamos melhorando** (coortes mensais) e **quanto demora** (velocidade).
*/

const PERIOD_OPTIONS = [
  { value: 90, label: "Últimos 90 dias" },
  { value: 180, label: "Últimos 6 meses" },
  { value: 365, label: "Último ano" },
  { value: 0, label: "Base completa" },
];

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const Kpi = ({ label, value, hint, tone = "neutral", delta }) => (
  <div className="flex-1 border-slate-200 px-5 py-4 sm:border-r sm:last:border-r-0">
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      <span className={`h-1.5 w-1.5 rounded-full ${toneOf(tone).dot}`} />
      {label}
    </p>
    <p className="mt-1 flex items-baseline gap-2">
      <span className="text-xl font-bold leading-none text-slate-900">{value}</span>
      {Number.isFinite(delta) ? (
        <span
          className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
            delta >= 0 ? "text-emerald-600" : "text-rose-600"
          }`}
        >
          {delta >= 0 ? <MdTrendingUp size={13} /> : <MdTrendingDown size={13} />}
          {Math.abs(Math.round(delta))}%
        </span>
      ) : null}
    </p>
    {hint ? <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p> : null}
  </div>
);

const SectionCard = ({ title, hint, action, children, className = "" }) => (
  <Card className={`overflow-hidden ${className}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-4 py-3">
      <div className="min-w-0">
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        {hint ? <p className="text-[11px] text-slate-500">{hint}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </Card>
);

const MiniTabela = ({ colunas, linhas, vazio }) => {
  if (!linhas.length) {
    return <p className="px-4 py-8 text-center text-xs text-slate-400">{vazio}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {colunas.map((coluna) => (
              <th
                key={coluna.key}
                className={`px-4 py-2 ${coluna.align === "right" ? "text-right" : ""}`}
              >
                {coluna.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {linhas.map((linha, index) => (
            <tr key={linha.key || index} className="hover:bg-slate-50/60">
              {colunas.map((coluna) => (
                <td
                  key={coluna.key}
                  className={`px-4 py-2 ${
                    coluna.align === "right"
                      ? "text-right font-semibold text-slate-700"
                      : "text-slate-700"
                  }`}
                >
                  {coluna.render(linha)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FunnelPage = () => {
  const [periodDays, setPeriodDays] = useState(365);

  const funnelQuery = useQuery(["crm", "funnel", periodDays], async () => {
    const params = new URLSearchParams();
    params.set(
      "registrationStartDate",
      periodDays > 0 ? isoDaysAgo(periodDays) : "2020-01-01"
    );
    const { data } = await axios.get(`${CRM.FUNNEL}?${params.toString()}`);
    return data.data;
  });

  const velocityQuery = useQuery(["crm", "velocity"], async () => {
    const { data } = await axios.get(`${CRM.FUNNEL_VELOCITY}?days=90`);
    return data.data;
  });

  const onboardingQuery = useQuery(["crm", "onboarding"], async () => {
    const { data } = await axios.get(`${CRM.FUNNEL_ONBOARDING}?days=30`);
    return data.data;
  });

  const funnel = funnelQuery.data;

  /*
    Variação da conversão entre os dois últimos meses FECHADOS. O mês corrente
    fica de fora: comparar 4 dias com 30 faz toda coorte parecer em queda.
  */
  const deltaConversao = (() => {
    const meses = funnel?.byMonth || [];
    if (meses.length < 3) return null;
    const fechado = meses[meses.length - 2];
    const anterior = meses[meses.length - 3];
    if (!anterior?.conversionRate) return null;
    return fechado.conversionRate - anterior.conversionRate;
  })();

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Funil de conversão</h1>
          <p className="text-sm text-slate-500">
            Contas entram pela data de CADASTRO; a conversão é medida pela
            situação atual delas.
          </p>
        </div>

        <select
          value={periodDays}
          onChange={(event) => setPeriodDays(Number(event.target.value))}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-pink-300"
        >
          {PERIOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </header>

      {funnelQuery.error ? (
        <ErrorBlock
          message="Não foi possível carregar o funil."
          onRetry={funnelQuery.refetch}
        />
      ) : funnelQuery.isLoading ? (
        <>
          <SkeletonKpiStrip count={5} withIcon={false} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SkeletonChart height={220} bars={4} className="xl:col-span-2" />
            <SkeletonPanel lines={6} />
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SkeletonChart height={180} bars={6} />
            <SkeletonChart height={180} bars={12} />
          </div>
        </>
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="flex flex-col divide-y divide-slate-100 sm:flex-row sm:divide-y-0">
              <Kpi
                label="Cadastros"
                value={formatNumber(funnel.overall.signups)}
                hint="contas que entraram no período"
              />
              <Kpi
                label="Ativação"
                value={formatPercent(funnel.overall.activationRate)}
                hint="cadastrou e chegou a agendar"
                tone={funnel.overall.activationRate >= 50 ? "success" : "warning"}
              />
              <Kpi
                label="Conversão"
                value={formatPercent(funnel.overall.conversionRate)}
                delta={deltaConversao}
                hint={`${formatNumber(funnel.overall.everPaid)} pagaram ao menos uma vez`}
                tone={funnel.overall.conversionRate > 0 ? "success" : "danger"}
              />
              <Kpi
                label="MRR da coorte"
                value={formatMoney(funnel.overall.mrr)}
                hint={`${formatNumber(funnel.overall.payingNow)} pagantes hoje`}
                tone="success"
              />
              <Kpi
                label="Retenção"
                value={formatPercent(funnel.overall.retentionRate)}
                hint="dos que pagaram, quantos ainda pagam"
                tone={funnel.overall.retentionRate >= 70 ? "success" : "warning"}
              />
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SectionCard
              className="xl:col-span-2"
              title="Onde o funil vaza"
              hint="Cada degrau é um subconjunto do anterior"
            >
              <FunnelShape steps={funnel.steps} />
            </SectionCard>

            <SectionCard
              title="Onde estão hoje"
              hint="Estágio atual das contas da coorte"
            >
              <div className="space-y-2 p-4">
                {funnel.stages
                  .filter((stage) => stage.count > 0)
                  .sort((a, b) => b.count - a.count)
                  .map((stage) => {
                    const largura =
                      (stage.count / Math.max(funnel.overall.signups, 1)) * 100;
                    return (
                      <div key={stage.id}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className="truncate text-xs font-medium text-slate-700"
                            title={stage.description}
                          >
                            {stage.label}
                          </span>
                          <span className="shrink-0 text-xs font-bold text-slate-900">
                            {formatNumber(stage.count)}
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${toneOf(stage.tone).bar}`}
                            style={{ width: `${Math.max(largura, 2)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard
              title="Por canal de aquisição"
              hint="Ordenado por conversão — o volume vai no rótulo"
            >
              <CanaisChart bySource={funnel.bySource} />
            </SectionCard>

            <SectionCard
              title="Mês a mês"
              hint="Quantas entraram e quantas delas pagaram"
            >
              <CoortesChart byMonth={funnel.byMonth} />
            </SectionCard>
          </div>

          {funnel.byCoupon?.length || funnel.byOwner?.length ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <SectionCard
                title="Por cupom"
                hint="Só contas que usaram cupom no cadastro"
              >
                <MiniTabela
                  vazio="Nenhuma conta com cupom no período."
                  linhas={(funnel.byCoupon || []).map((r) => ({ ...r, key: r.coupon }))}
                  colunas={[
                    { key: "coupon", label: "Cupom", render: (r) => r.coupon },
                    {
                      key: "signups",
                      label: "Cadastros",
                      align: "right",
                      render: (r) => formatNumber(r.signups),
                    },
                    {
                      key: "everPaid",
                      label: "Pagaram",
                      align: "right",
                      render: (r) => formatNumber(r.everPaid),
                    },
                    {
                      key: "conversionRate",
                      label: "Conversão",
                      align: "right",
                      render: (r) => formatPercent(r.conversionRate),
                    },
                  ]}
                />
              </SectionCard>

              <SectionCard
                title="Por responsável"
                hint="Resultado da carteira de cada pessoa"
              >
                <MiniTabela
                  vazio="Nenhuma conta com responsável definido."
                  linhas={(funnel.byOwner || []).map((r) => ({
                    ...r,
                    key: r.ownerUserId,
                  }))}
                  colunas={[
                    { key: "ownerName", label: "Responsável", render: (r) => r.ownerName },
                    {
                      key: "signups",
                      label: "Contas",
                      align: "right",
                      render: (r) => formatNumber(r.signups),
                    },
                    {
                      key: "everPaid",
                      label: "Converteram",
                      align: "right",
                      render: (r) => formatNumber(r.everPaid),
                    },
                    {
                      key: "mrr",
                      label: "MRR",
                      align: "right",
                      render: (r) => formatMoney(r.mrr),
                    },
                  ]}
                />
              </SectionCard>
            </div>
          ) : null}
        </>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionCard
          title="Velocidade de conversão"
          hint="Movimentos de estágio nos últimos 90 dias"
        >
          {velocityQuery.isLoading ? (
            <SkeletonRows rows={2} className="p-4" />
          ) : velocityQuery.error ? (
            <ErrorBlock message="Não foi possível carregar a velocidade." />
          ) : velocityQuery.data.conversions === 0 &&
            velocityQuery.data.churns === 0 ? (
            <EmptyState
              icon={MdOutlineInsights}
              title="Sem histórico de movimento ainda"
              description="Sai das mudanças de estágio registradas pelo job diário. Depois da primeira semana rodando ele passa a ter o que mostrar."
            />
          ) : (
            <div className="p-4">
              <div className="flex divide-x divide-slate-100">
                {[
                  {
                    label: "Converteram",
                    value: velocityQuery.data.conversions,
                    tone: "text-emerald-600",
                  },
                  {
                    label: "Perdidas",
                    value: velocityQuery.data.churns,
                    tone: "text-rose-600",
                  },
                  {
                    label: "Saldo",
                    value: velocityQuery.data.netMovement,
                    tone:
                      velocityQuery.data.netMovement >= 0
                        ? "text-emerald-600"
                        : "text-rose-600",
                  },
                ].map((item) => (
                  <div key={item.label} className="flex-1 px-4 first:pl-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {item.label}
                    </p>
                    <p className={`text-xl font-bold leading-tight ${item.tone}`}>
                      {formatNumber(item.value)}
                    </p>
                  </div>
                ))}
              </div>

              {velocityQuery.data.timeToPaidByOrigin.length ? (
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Tempo até pagar, por estágio de origem
                  </p>
                  <ul className="space-y-1.5">
                    {velocityQuery.data.timeToPaidByOrigin.map((row) => (
                      <li
                        key={row.from}
                        className="flex items-center justify-between gap-3 text-sm"
                      >
                        <span className="text-slate-700">{row.fromLabel}</span>
                        <span className="flex items-center gap-2">
                          <span className="font-bold text-slate-900">
                            {row.avgDays} dias
                          </span>
                          {/*
                            Amostra pequena vem com alerta: uma média de 3 casos
                            não deve ser lida como a de 300.
                          */}
                          <span
                            className={`text-[11px] ${
                              row.samples < 10
                                ? "font-semibold text-amber-700"
                                : "text-slate-400"
                            }`}
                            title={
                              row.samples < 10
                                ? "Amostra pequena — leia com cautela"
                                : undefined
                            }
                          >
                            {row.samples < 10 ? (
                              <MdOutlineInfo className="mr-0.5 inline" size={12} />
                            ) : null}
                            {formatNumber(row.samples)} caso(s)
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Abandono no cadastro"
          hint="Sessões distintas por tela do onboarding, últimos 30 dias"
          action={
            onboardingQuery.data?.entrySessions ? (
              <Badge tone="neutral">
                {formatNumber(onboardingQuery.data.entrySessions)} sessões
              </Badge>
            ) : null
          }
        >
          {onboardingQuery.isLoading ? (
            <SkeletonRows rows={3} className="p-4" />
          ) : onboardingQuery.error ? (
            <ErrorBlock message="Não foi possível carregar o funil de cadastro." />
          ) : !onboardingQuery.data.steps.length ? (
            <EmptyState
              icon={MdOutlineInsights}
              title="Sem telemetria de onboarding no período"
              description="Nenhum evento de cadastro foi registrado nos últimos 30 dias."
            />
          ) : (
            <div className="p-4">
              <OnboardingChart steps={onboardingQuery.data.steps} />
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default FunnelPage;
